<template>
    <div v-if="visible" class="professional-detail-overlay" @click.self="$emit('close')">
        <section class="professional-detail-dialog" :data-professional="professionalType" role="dialog" aria-modal="true" :aria-labelledby="titleId">
            <header class="professional-detail-header">
                <div>
                    <p>APPLICATION REVIEW</p>
                    <h4 :id="titleId">{{ roleLabel }}审核详情</h4>
                </div>
                <button type="button" :aria-label="`关闭${roleLabel}审核详情`" @click="$emit('close')">×</button>
            </header>

            <main class="professional-detail-main">
                <p v-if="detailViewState === 'loading'" class="professional-detail-copy">正在加载申请审核详情…</p>
                <p v-else-if="detailViewState === 'error'" class="professional-detail-error">{{ error }}</p>
                <section v-else-if="detailViewState === 'content'" class="professional-detail-content" :aria-label="`${roleLabel}申请审核详情`">
                    <section class="professional-detail-summary">
                        <h5>申请信息</h5>
                        <dl class="professional-detail-summary-list">
                            <div><dt>申请编号</dt><dd>{{ detail.applicationId || '—' }}</dd></div>
                            <div><dt>申请类型</dt><dd>{{ detail.applicationTypeDescription || '—' }}</dd></div>
                            <div><dt>审核状态</dt><dd :class="reviewStatusClass">{{ reviewStatusText }}</dd></div>
                            <div><dt>姓名</dt><dd>{{ detail.creatorName || '—' }}</dd></div>
                            <div v-if="isInitialApplication"><dt>联系电话</dt><dd>{{ detail.phone || '—' }}</dd></div>
                            <div v-if="isInitialApplication"><dt>服务地区</dt><dd>{{ detail.region || '—' }}</dd></div>
                            <div><dt>提交时间</dt><dd>{{ detail.submittedAt || '—' }}</dd></div>
                            <div><dt>完成时间</dt><dd>{{ detail.finishedAt || '—' }}</dd></div>
                        </dl>
                    </section>
                    <p v-if="shouldShowReviewReason" class="professional-review-reason">驳回原因：{{ detail.reviewReason }}</p>
                    <p v-if="showNoStyleQualification" class="professional-qualification-warning">暂不具备风格接单资格</p>

                    <section v-if="isPortraitReviewApplication && detail.personalPhoto" class="professional-detail-section">
                        <h5>本人真实照片审核</h5>
                        <professional-application-attachment-list title="本次提交的本人真实照片" :attachments="[detail.personalPhoto]" @image-error="$emit('reload')" />
                    </section>

                    <section v-if="hasApplicationProfile" class="professional-detail-section">
                        <h5>基础资料</h5>
                        <professional-application-attachment-list v-if="detail.signedAgreement" title="已签署协议" :attachments="[detail.signedAgreement]" />
                    </section>

                    <section v-if="isPhotographer && (isInitialApplication || detail.styles.length)" class="professional-detail-section">
                        <h5>摄影风格</h5>
                        <p v-if="!detail.styles.length" class="professional-detail-copy">暂无风格资料。</p>
                        <article v-for="(style, index) in detail.styles" :key="`style-${index}`" class="professional-review-item">
                            <professional-application-review-status :item="style" />
                            <strong>{{ [style.themeName, style.styleName].filter(Boolean).join(' · ') || '摄影风格' }}</strong>
                            <professional-application-attachment-list title="原创样片" :attachments="style.samples" />
                        </article>
                    </section>

                    <section v-if="isPhotographer && (isInitialApplication || detail.equipments.length)" class="professional-detail-section">
                        <h5>摄影设备</h5>
                        <p v-if="!detail.equipments.length" class="professional-detail-copy">暂无设备资料。</p>
                        <article v-for="(equipment, index) in detail.equipments" :key="`equipment-${index}`" class="professional-review-item">
                            <professional-application-review-status :item="equipment" />
                            <strong>{{ [equipment.equipmentType, equipment.brandModel].filter(Boolean).join(' · ') || '摄影设备' }}</strong>
                            <professional-application-attachment-list title="设备证明图" :attachments="equipment.proofImages" />
                        </article>
                    </section>

                    <section v-if="isInitialApplication && isMakeupArtist" class="professional-detail-section">
                        <h5>原创化妆作品</h5>
                        <professional-application-attachment-list title="原创作品" :attachments="detail.originalWorks" empty-copy="暂无可展示的原创作品。" />
                    </section>

                    <section v-if="isInitialApplication || detail.certificates.length" class="professional-detail-section">
                        <h5>专业证书</h5>
                        <p v-if="!detail.certificates.length" class="professional-detail-copy">暂无证书资料。</p>
                        <article v-for="(certificate, index) in detail.certificates" :key="`certificate-${index}`" class="professional-review-item">
                            <professional-application-review-status :item="certificate" />
                            <strong>{{ certificate.certificateName || '专业证书' }}</strong>
                            <professional-application-attachment-list title="证书附件" :attachments="certificate.file ? [certificate.file] : []" />
                        </article>
                    </section>
                </section>
                <p v-else class="professional-detail-copy">暂无可展示的审核详情。</p>
            </main>
            <footer v-if="canResubmit || canWithdraw" class="professional-detail-footer">
                <button v-if="canWithdraw" type="button" class="professional-detail-withdraw" :disabled="withdrawing" @click="$emit('withdraw')">{{ withdrawing ? '正在撤回…' : '撤回申请' }}</button>
                <button v-if="canResubmit" type="button" @click="$emit('resubmit')">重新提交申请</button>
            </footer>
        </section>
    </div>
</template>

<script>
import {
    canShowProfessionalApplicationReviewReason,
    getProfessionalApplicationDetailViewState,
    isRolePortraitReviewApplication,
} from "@/tool/creatorCenter.cjs";
import ProfessionalApplicationAttachmentList from "./ProfessionalApplicationAttachmentList";
import ProfessionalApplicationReviewStatus from "./ProfessionalApplicationReviewStatus";

export default {
    name: "ProfessionalApplicationDetailDialog",
    components: {
        ProfessionalApplicationAttachmentList,
        ProfessionalApplicationReviewStatus,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: "",
        },
        detail: {
            type: Object,
            default: null,
        },
        professionalType: {
            type: String,
            required: true,
        },
        showResubmit: {
            type: Boolean,
            default: false,
        },
        showWithdraw: {
            type: Boolean,
            default: false,
        },
        withdrawing: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isPhotographer() {
            return this.professionalType === "photographer";
        },
        isMakeupArtist() {
            return this.professionalType === "makeupArtist";
        },
        roleLabel() {
            return this.isPhotographer ? "摄影师" : "化妆师";
        },
        titleId() {
            return this.isPhotographer ? "photographerReviewDetailTitle" : "makeupArtistReviewDetailTitle";
        },
        detailViewState() {
            return getProfessionalApplicationDetailViewState({
                loading: this.loading,
                error: this.error,
                detail: this.detail,
            });
        },
        hasApplicationProfile() {
            return this.isInitialApplication && !!(this.detail && (this.detail.phone || this.detail.region || this.detail.signedAgreement));
        },
        isInitialApplication() {
            return Number(this.detail && this.detail.applicationType) === 1;
        },
        isPortraitReviewApplication() {
            return isRolePortraitReviewApplication(this.detail && this.detail.applicationType);
        },
        shouldShowReviewReason() {
            return canShowProfessionalApplicationReviewReason(
                this.detail && this.detail.applicationType,
                this.detail && this.detail.applicationStatus,
                this.detail && this.detail.reviewReason,
            );
        },
        reviewStatusText() {
            return this.detail.applicationStatusDescription || "状态待确认";
        },
        reviewStatusClass() {
            const status = Number(this.detail && this.detail.applicationStatus);
            if (status === 2) return "is-approved";
            if (status === 3) return "is-rejected";
            if (status === 1) return "is-pending";
            return "";
        },
        showNoStyleQualification() {
            return this.isPhotographer && this.detail &&
                Number(this.detail.applicationStatus) === 2 &&
                this.detail.hasStyleOrderQualification === false;
        },
        canResubmit() {
            return this.showResubmit && this.detail &&
                Number(this.detail.applicationType) === 1 &&
                [3, 4].indexOf(Number(this.detail.applicationStatus)) >= 0;
        },
        canWithdraw() {
            return this.showWithdraw && this.detail && Number(this.detail.applicationStatus) === 1;
        },
    },
};
</script>

<style>
.professional-detail-overlay { position: fixed; z-index: 70; inset: 0; display: grid; place-items: center; padding: 24px; background: rgba(32, 27, 41, .56); }.professional-detail-dialog { display: flex; width: min(720px, calc(100vw - 48px)); max-height: min(760px, calc(100vh - 48px)); flex-direction: column; overflow: hidden; color: #332d49; background: #fff; border: 1px solid #d9e9f1; border-radius: 20px; box-shadow: 0 24px 70px rgba(30, 42, 58, .28); }.professional-detail-header { display: flex; flex: 0 0 auto; align-items: flex-start; justify-content: space-between; gap: 18px; padding: 19px 22px 16px; border-bottom: 1px solid #e7eff3; }.professional-detail-header p { margin: 0 0 4px; color: #5591ae; font-size: 10px; font-weight: 800; letter-spacing: .12em; }.professional-detail-header h4 { margin: 0; color: #3f3158; font-size: 20px; font-weight: 800; }.professional-detail-header button { display: grid; width: 30px; height: 30px; flex: 0 0 auto; place-items: center; padding: 0; color: #5d6070; background: #f1f6f8; border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 22px; line-height: 1; }.professional-detail-main { min-height: 0; padding: 20px 22px 24px; overflow-y: auto; overscroll-behavior: contain; }.professional-detail-content { display: grid; gap: 18px; }.professional-detail-summary { display: grid; gap: 11px; padding: 16px; background: #f7fafc; border: 1px solid #e1ecf1; border-radius: 12px; }.professional-detail-summary h5 { margin: 0; color: #4b6f83; font-size: 13px; }.professional-detail-summary-list { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 9px 18px; margin: 0; }.professional-detail-summary-list > div { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 7px; align-items: baseline; min-width: 0; color: #514a5a; font-size: 12px; line-height: 1.6; }.professional-detail-summary-list dt { color: #8a8290; white-space: nowrap; }.professional-detail-summary-list dd { min-width: 0; margin: 0; word-break: break-word; }.professional-detail-summary-list dd.is-approved { color: #31735a; }.professional-detail-summary-list dd.is-rejected { color: #a0475c; }.professional-detail-summary-list dd.is-pending { color: #916022; }.professional-review-reason, .professional-detail-error { margin: -10px 0 0; color: #a1495c; font-size: 12px; line-height: 1.6; }.professional-qualification-warning { margin: -10px 0 0; padding: 7px 9px; color: #915e22; background: #fff5dc; border-radius: 7px; font-size: 11px; font-weight: 800; }.professional-detail-section { display: grid; gap: 10px; padding-top: 17px; border-top: 1px solid #edf1f3; }.professional-detail-section h5, .professional-attachments > strong { margin: 0; color: #4b6f83; font-size: 13px; }.professional-detail-list { display: grid; gap: 8px; margin: 0; }.professional-detail-list > div { display: grid; grid-template-columns: 74px minmax(0, 1fr); gap: 10px; font-size: 12px; line-height: 1.6; }.professional-detail-list dt { color: #8a8290; }.professional-detail-list dd { min-width: 0; margin: 0; color: #514a5a; word-break: break-word; }.professional-intro { white-space: pre-wrap; }.professional-review-item { display: grid; gap: 8px; padding: 11px; background: #f8fafb; border-radius: 10px; }.professional-review-item > strong { color: #5a5261; font-size: 12px; }.professional-review-status-wrap { display: flex; flex-wrap: wrap; gap: 7px; align-items: baseline; }.professional-review-status-wrap small { color: #a1495c; font-size: 11px; }.professional-item-status { display: inline-flex; width: fit-content; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 800; }.professional-item-status.review { color: #916022; background: #fff1c9; }.professional-item-status.approved { color: #31735a; background: #e6f7ed; }.professional-item-status.rejected { color: #a0475c; background: #fff0f3; }.professional-attachments { display: grid; gap: 8px; }.professional-attachment-list { display: flex; flex-wrap: wrap; gap: 8px; }.professional-attachment-image { width: 96px; height: 96px; overflow: hidden; border: 1px solid #dce8ed; border-radius: 8px; cursor: zoom-in; }.professional-image-placeholder { display: grid; width: 100%; height: 100%; place-items: center; color: #8c96a0; background: #f4f7f8; font-size: 10px; }.professional-attachment-link { display: inline-flex; align-items: center; min-height: 34px; padding: 0 10px; color: #377b9a; background: #eef8fc; border-radius: 7px; font-size: 12px; text-decoration: none; }.professional-attachment-link:hover { color: #fff; background: #4f90ae; }.professional-detail-copy { margin: 0; color: #857f76; font-size: 12px; line-height: 1.65; }.professional-detail-dialog button:focus-visible, .professional-attachment-link:focus-visible { outline: 2px solid #4f90ae; outline-offset: 2px; }
.professional-detail-header > button { line-height: 1 !important; text-align: center; }
@media (max-width: 620px) { .professional-detail-overlay { padding: 12px; }.professional-detail-dialog { width: 100%; max-height: calc(100vh - 24px); border-radius: 16px; }.professional-detail-header, .professional-detail-main { padding-right: 16px; padding-left: 16px; }.professional-detail-summary-list { grid-template-columns: 1fr; }.professional-detail-list > div { grid-template-columns: 1fr; gap: 2px; }.professional-attachment-image { width: 76px; height: 76px; } }
.professional-detail-footer { display: flex; flex: 0 0 auto; justify-content: flex-end; padding: 14px 22px; border-top: 1px solid #e7eff3; }.professional-detail-footer button { width: auto; min-height: 36px; padding: 7px 14px; color: #fff; background: #d56886; border: 1px solid #d56886; border-radius: 9px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 800; }.professional-detail-footer button:hover { color: #fff; background: #bd5471; border-color: #bd5471; }.professional-detail-withdraw { background: #4f90ae !important; border-color: #4f90ae !important; }.professional-detail-withdraw:hover { background: #3d7f9d !important; border-color: #3d7f9d !important; }.professional-detail-dialog[data-professional="makeupArtist"] .professional-detail-withdraw { background: #d56886 !important; border-color: #d56886 !important; }.professional-detail-dialog[data-professional="makeupArtist"] .professional-detail-withdraw:hover { background: #bd5471 !important; border-color: #bd5471 !important; }
</style>
